import { faAngleDown, faCalendarDay, faList } from '@fortawesome/pro-regular-svg-icons';
import Pet from '@spike/pet-model';
import { ActivityTimeline } from 'components/Activities/Timeline/ActivityTimeline';
import { ActivityTimelineProvider, useActivityTimeline } from 'components/Activities/Timeline/ActivityTimelineProvider';
import { ACTIVITY_CREATE_OPTIONS, ACTIVITY_FILTER_OPTIONS } from 'components/Activities/Icon/ActivityIcon';
import styles from 'components/Pet/Activities/PetActivities.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { SelectDropdown } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { useTimeZone } from 'hooks';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import useIsMobile from 'hooks/useIsMobile';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface PetActivitiesProps {
    pet: Pet;
    customerId: number;
}

export const PetActivities: React.FC<PetActivitiesProps> = ({ pet, customerId }) => {
    const pets = useSelector((state: RootState) => state.pets.list);

    if (!pet.id) {
        return null;
    }

    return (
        <div className={styles.root}>
            <ActivityTimelineProvider
                pet={pet}
                pets={pets}
                customerId={customerId}
            >
                <ActivityTimeline
                    infiniteScrollTrigger={document.querySelector('#client-page-body-id')}
                    classNames={{
                        timeline: styles.timeline
                    }}
                >
                    <PetActivitiesHeader />
                    <PetActivitiesFilters />
                </ActivityTimeline>
            </ActivityTimelineProvider>
        </div>
    );
};

const PetActivitiesHeader: React.FC = () => {
    return (
        <div className={styles.header}>
            <DefaultHeader
                title="Activities"
                textBelowTitle="Capture every activity and share with clients only if you want."
                customButton={<AddActivityButton className={styles.onlyMobile} />}
            />
        </div>
    );
};

const PetActivitiesFilters: React.FC = () => {
    const isMobile = useIsMobile();
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, allTimeOption } = useDateRangeOptions();
    const { activityTypes, activityTypesChangeHandler, datePeriod, datePeriodChangeHandler } = useActivityTimeline();

    return (
        <div className={styles.timelineHeader}>
            <div className={styles.timelineFilters}>
                <SelectDropdown
                    multiple
                    showSelectAll
                    label="Activities"
                    options={ACTIVITY_FILTER_OPTIONS}
                    value={ACTIVITY_FILTER_OPTIONS.filter(item => activityTypes.includes(item.id))}
                    onChange={types => activityTypesChangeHandler(types.map(item => item.id))}
                    buttonProps={{
                        size: 'large',
                        leftIcon: faList,
                        variant: 'outlined',
                        onlyIcon: isMobile
                    }}
                />

                <DateRangePickerDropdown
                    timeZone={timeZone}
                    value={datePeriod}
                    onChange={datePeriodChangeHandler}
                    options={[todayOption, thisMonthOption, last30DaysOption, allTimeOption]}
                    buttonProps={{
                        size: 'large',
                        leftIcon: faCalendarDay,
                        variant: 'outlined',
                        onlyIcon: isMobile
                    }}
                />
            </div>

            <div className={styles.timelineActions}>
                <AddActivityButton size="large" />
            </div>
        </div>
    );
};

const AddActivityButton: React.FC<{
    size?: 'large' | 'medium';
    className?: string;
}> = ({ size = 'medium', className }) => {
    const { isPetEnabled, openActivityForm } = useActivityTimeline();

    return (
        <Dropdown>
            <DropdownTrigger className={className}>
                <Button
                    label="Add"
                    size={size}
                    disabled={!isPetEnabled}
                    rightIcon={faAngleDown}
                />
            </DropdownTrigger>

            <DropdownContent
                minWidth={130}
                align="end"
            >
                {ACTIVITY_CREATE_OPTIONS.map(option => (
                    <DropdownItem
                        key={option.id}
                        leftIcon={option.icon}
                        onSelect={() => openActivityForm(option.id)}
                    >
                        {option.label}
                    </DropdownItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );
};
